import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const createExpenseSchema = (isAdmin: boolean) => z.object({
  category_id: z.string().min(1, 'Category is required'),
  title: z.string().min(1, 'Title is required').max(255, 'Title must be less than 255 characters'),
  description: z.string().optional(),
  amount: z.number().min(0.01, 'Amount must be greater than 0'),
  expense_date: z.date({
    required_error: 'Expense date is required',
  }),
  expense_type: z.enum(['company', 'personal', 'reimbursement']),
  payment_method: z.enum(['cash', 'bank_transfer', 'credit_card', 'debit_card', 'cheque', 'other']),
  receipt_number: z.string().optional(),
  notes: z.string().optional(),
  branch_id: isAdmin ? z.string().min(1, 'Branch is required') : z.string().optional(),
});

type ExpenseFormData = z.infer<ReturnType<typeof createExpenseSchema>>;

interface ExpenseCategory {
  id: string;
  name: string;
  description?: string;
  color: string;
}

interface Expense {
  id: string;
  category_id: string;
  title: string;
  description?: string;
  amount: number;
  expense_date: string;
  expense_type: 'company' | 'personal' | 'reimbursement';
  payment_method: 'cash' | 'bank_transfer' | 'credit_card' | 'debit_card' | 'cheque' | 'other';
  receipt_number?: string;
  notes?: string;
  branch_id?: string;
}

interface Branch {
  id: string;
  name: string;
  isMain?: boolean;
}

interface ExpenseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'add' | 'edit';
  expense?: Expense;
  categories: ExpenseCategory[];
  branches: Branch[];
  isAdmin: boolean;
  userBranchId?: string;
  onSubmit: (data: ExpenseFormData) => void;
}

export default function ExpenseDialog({
  open,
  onOpenChange,
  mode,
  expense,
  categories,
  branches,
  isAdmin,
  userBranchId,
  onSubmit,
}: ExpenseDialogProps) {
  const [loading, setLoading] = useState(false);
  
  // Debug log - remove after testing
  console.log('ExpenseDialog received categories:', categories?.length || 0, 'items');
  console.log('Categories data:', categories);
  console.log('Dialog open state:', open);

  // Find main branch ID for admin users, use user's branch for non-admin
  const mainBranch = branches.find(branch => branch.isMain);
  const defaultBranchId = isAdmin ? (mainBranch?.id || branches[0]?.id || '') : userBranchId;

  const form = useForm<ExpenseFormData>({
    resolver: zodResolver(createExpenseSchema(isAdmin)),
    defaultValues: {
      category_id: '',
      title: '',
      description: '',
      amount: 0,
      expense_date: new Date(),
      expense_type: 'company',
      payment_method: 'cash',
      receipt_number: '',
      notes: '',
      branch_id: defaultBranchId,
    },
  });

  useEffect(() => {
    if (expense && mode === 'edit') {
      form.reset({
        category_id: expense.category_id || '',
        title: expense.title || '',
        description: expense.description || '',
        amount: parseFloat(expense.amount) || 0,
        expense_date: new Date(expense.expense_date),
        expense_type: expense.expense_type || 'company',
        payment_method: expense.payment_method || 'cash',
        receipt_number: expense.receipt_number || '',
        notes: expense.notes || '',
        branch_id: expense.branch_id || defaultBranchId,
      });
    } else if (mode === 'add') {
      form.reset({
        category_id: '',
        title: '',
        description: '',
        amount: 0,
        expense_date: new Date(),
        expense_type: 'company',
        payment_method: 'cash',
        receipt_number: '',
        notes: '',
        branch_id: defaultBranchId,
      });
    }
  }, [expense, mode, form, defaultBranchId]);

  const handleSubmit = async (data: ExpenseFormData) => {
    setLoading(true);
    try {
      // Convert date to YYYY-MM-DD format for backend
      const formattedData = {
        ...data,
        expense_date: format(data.expense_date, 'yyyy-MM-dd'),
        // Ensure non-admin users always have their branch_id set
        branch_id: isAdmin ? data.branch_id : userBranchId,
      };

      // Remove amount field when editing to prevent backend error
      if (mode === 'edit') {
        delete formattedData.amount;
      }
      
      await onSubmit(formattedData);
      form.reset();
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'add' ? 'Add New Expense' : 'Edit Expense'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'add' 
              ? 'Create a new expense record.'
              : 'Update the expense details.'
            }
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="category_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value} key={`categories-${categories?.length || 0}`}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {categories && categories.length > 0 ? (
                          categories.map((category) => (
                            <SelectItem key={category.id} value={category.id}>
                              <div className="flex items-center">
                                <div
                                  className="w-3 h-3 rounded-full mr-2"
                                  style={{ backgroundColor: category.color }}
                                />
                                {category.name}
                              </div>
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="" disabled>
                            Loading categories...
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="expense_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expense Type *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="company">Company Expense</SelectItem>
                        <SelectItem value="personal">Personal Expense</SelectItem>
                        <SelectItem value="reimbursement">Reimbursement</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isAdmin && (
                <FormField
                  control={form.control}
                  name="branch_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Branch *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value} key={`branches-${branches?.length || 0}`}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select branch" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {branches && branches.length > 0 ? (
                            branches.map((branch) => (
                              <SelectItem key={branch.id} value={branch.id}>
                                <div className="flex items-center">
                                  {branch.name}
                                  {branch.isMain && (
                                    <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded">Main</span>
                                  )}
                                </div>
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="" disabled>
                              Loading branches...
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title *</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter expense title" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter expense description"
                      {...field}
                      rows={3}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        disabled={mode === 'edit'}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="expense_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expense Date *</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-full pl-3 text-left font-normal',
                              !field.value && 'text-muted-foreground'
                            )}
                          >
                            {field.value ? (
                              format(field.value, 'PPP')
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) =>
                            date > new Date() || date < new Date('1900-01-01')
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="payment_method"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Payment Method *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="credit_card">Credit Card</SelectItem>
                        <SelectItem value="debit_card">Debit Card</SelectItem>
                        <SelectItem value="cheque">Cheque</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="receipt_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Receipt Number</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter receipt number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Additional notes or comments"
                      {...field}
                      rows={2}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : mode === 'add' ? 'Create Expense' : 'Update Expense'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}